import { ZButton } from "@/shared/components/button";
import { ZTableColumnProps } from "@/shared/components/table";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { Avatar, AvatarGroup, Tooltip } from "@nextui-org/react";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { SeverityChip } from "../components/SeverityChip";
import { RiskItem, SourceReference } from "../types/risks";

// Vendor cell component with custom fallback styling
interface VendorCellProps {
  vendor_icon: string;
  vendor: string;
  product: string;
}

function VendorCell({ vendor_icon, vendor, product }: VendorCellProps) {
  const firstLetter = vendor?.trim().charAt(0).toUpperCase() || '?';
  const displayText = product && product.trim()
    ? `${vendor} ${product}`
    : vendor;

  return (
    <div className="flex items-center gap-2 min-h-[40px] text-[#333]">
      {vendor_icon ? (
        <VendorLogo
          src={vendor_icon}
          alt={vendor}
          size={20}
          needsAuthToken={true}
        />
      ) : (
        <div
          className="flex items-center justify-center"
          style={{
            width: '20px',
            height: '20px',
            background: '#FAFAFA',
            border: '1px solid #E4E4E7',
            borderRadius: '24px',
            padding: '2px',
          }}
        >
          <span
            style={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '16px',
              textAlign: 'center',
              color: '#71717A',
            }}
          >
            {firstLetter}
          </span>
        </div>
      )}
      <span className="text-sm font-medium">{displayText}</span>
    </div>
  );
}

// Separate component to properly use React hooks
interface ReferencesCellProps {
  sources: SourceReference[];
  id: string;
}

function ReferencesCell({ sources, id }: ReferencesCellProps) {
  const [tooltipStates, setTooltipStates] = useState<{ [key: string]: boolean }>({});

  const handleOpenChange = useCallback((key: string, open: boolean) => {
    setTooltipStates((prev) => ({ ...prev, [key]: open }));
  }, []);

  const handleViewClick = useCallback(
    (url: string, key: string) => {
      handleOpenChange(key, false);
      window.open(url, "_blank");
    },
    [handleOpenChange]
  );

  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <AvatarGroup
        isBordered
        classNames={{
          count:
            "flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE] bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0 data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary w-4 h-4 text-tiny",
        }}
        color="primary"
      >
        {sources.map((source, index) => {
          const tooltipKey = `${id}_${index}_${source.indices[0]}`;
          return (
            <Tooltip
              showArrow={true}
              placement="top"
              shouldFlip={true}
              key={tooltipKey}
              isDismissable={true}
              closeDelay={100}
              isOpen={tooltipStates[tooltipKey]}
              onOpenChange={(open) => handleOpenChange(tooltipKey, open)}
              classNames={{
                content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
                arrow: "backdrop-blur-md",
                base: "before:bg-black/95",
              }}
              content={
                <div className="flex flex-col gap-y-[0.5rem] max-w-96">
                  <div className="max-h-[5rem] overflow-hidden">
                    <p className="line-clamp-4 text-white break-all">{source.url}</p>
                  </div>
                  <div className="flex gap-3">
                    <p className="text-tiny text-[#A5A5A5] break-all">{source.domain}</p>
                  </div>
                  <div className="self-end flex gap-2">
                    <ZButton size="sm" type="button" onClick={() => handleViewClick(source.url, tooltipKey)}>
                      View Reference
                    </ZButton>
                  </div>
                </div>
              }
            >
              <Avatar
                key={tooltipKey}
                classNames={{
                  icon: "[&>span]:text-primary",
                  base: `
                      flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none
                      data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus
                      data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE]
                      bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0
                      data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform
                      data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary
                      w-4 h-4 text-[10px]
                    `,
                }}
                icon={<span>{source.indices[0]}</span>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenChange(tooltipKey, !tooltipStates[tooltipKey]);
                }}
              >
                {source.indices[0]}
              </Avatar>
            </Tooltip>
          );
        })}
      </AvatarGroup>
    </div>
  );
}

export const RisksColumns: ZTableColumnProps<RiskItem>[] = [
  {
    type: "custom",
    fieldName: "third_party_product",
    headerName: "Third-Party & Product",
    sortable: true,
    getSortValue: ({ vendor }) => vendor,
    renderCell: ({ vendor_icon, vendor, product }) => (
      <VendorCell vendor_icon={vendor_icon} vendor={vendor} product={product} />
    ),
  },
  {
    type: "custom",
    fieldName: "reporting_date",
    headerName: "Reporting Date",
    sortable: true,
    getSortValue: ({ reporting_date }) => dayjs(reporting_date).toDate().getTime(),
    renderCell: ({ reporting_date }) => (
      <div className="text-sm text-[#333]">
        {dayjs(reporting_date).format("DD MMM YYYY")}
      </div>
    ),
  },
  {
    type: "custom",
    fieldName: "control_title",
    headerName: "Control Title",
    renderCell: ({ control_title }) => (
      <div className="py-2">
        <p className="text-sm text-gray-600 leading-relaxed">{control_title}</p>
      </div>
    ),
  },
  {
    type: "custom",
    fieldName: "gap",
    headerName: "Gap",
    renderCell: ({ gap }) => (
      <div className="py-2">
        <p className="text-sm text-gray-600 leading-relaxed">{gap}</p>
      </div>
    ),
  },
  {
    type: "custom",
    fieldName: "severity",
    headerName: "Severity",
    sortable: true,
    getSortValue: ({ severity_level }) => severity_level,
    renderCell: ({ severity }) => <SeverityChip severity={severity} />,
  },
  {
    type: "custom",
    fieldName: "recommendations",
    headerName: "Recommendations",
    renderCell: ({ recommendations }) => (
      <div className="py-2">
        <p className="text-sm text-gray-600 leading-relaxed">{recommendations}</p>
      </div>
    ),
  },
  {
    type: "custom",
    fieldName: "references",
    headerName: "References",
    renderCell: ({ sources, id }) => <ReferencesCell sources={sources} id={id} />,
  },
];
